import { motion } from 'framer-motion';
import { ChevronRight, MoreHorizontal, SettingsIcon } from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drawer } from 'vaul';
import Text from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { IntercomLauncher } from '../../intercom/components/intercom-launcher.tsx';
import type { MenuItem, MenuSubItem, NavPathOverrideEnum } from '../contracts.ts';
import { usePlatoLayoutNavigation } from '../hooks/use-plato-layout-navigation.tsx';
import { Menu, MenuBackRow } from './menu-primitives.tsx';
import { createIsSelected } from './navigation-helpers.ts';
import { PushStack } from './push-stack.tsx';
import {
    DefaultMetricLevel,
    DefaultTimeframeLevel,
    LanguageLevel,
    SettingsRows,
    settingsSubLevels,
    ViewAsLevel,
} from './settings-menu.tsx';

type NavLinkItem = MenuSubItem | (MenuItem & { href: string });

function flattenItem(item: MenuItem): NavLinkItem[] {
    if (item.subItems) {
        return item.subItems;
    }
    if (item.href) {
        return [{ ...item, href: item.href }];
    }
    return [];
}

// ponytail: damping ≥ 2√stiffness = critically damped → no overshoot
const spring = { type: 'spring', stiffness: 380, damping: 40 } as const;

type NavTabProps = {
    active: boolean;
    label: string;
    icon: ComponentType<{ className?: string }>;
};

function NavTab({ active, label, icon: Icon }: NavTabProps): ReactNode {
    // we use the grid row height trick to animate with pure CSS:
    // see https://css-tricks.com/css-grid-can-do-auto-height-transitions/
    return (
        <>
            <motion.div className="relative z-10" animate={{ scale: active ? 1.1 : 1 }} transition={spring}>
                <Icon className="h-6! w-6!" />
            </motion.div>
            <span
                className={cn(
                    'relative z-10 grid text-[10px] font-semibold tracking-[0.05em] transition-all duration-200',
                    active ? 'ml-1.5 grid-cols-[1fr] opacity-100' : 'grid-cols-[0fr] opacity-0',
                )}
            >
                <span className="min-w-0 overflow-hidden whitespace-nowrap">{label}</span>
            </span>
        </>
    );
}

export function MobileBottomNav() {
    const [moreOpen, setMoreOpen] = useState(false);
    /** 0 = nav, 1 = settings, then one per `settingsSubLevels` entry in order. */
    const [settingsLevel, setSettingsLevel] = useState(0);
    const { t } = useLocale('common');
    const location = useLocation();
    const { navigation, activeSection } = usePlatoLayoutNavigation();
    const sendEvent = useAnalytics();

    // Optimistic active section: set the instant a primary tab is tapped so the
    // highlight moves immediately, decoupled from React Router
    const [pendingId, setPendingId] = useState<NavPathOverrideEnum | undefined>(undefined);

    // Drop the optimistic id once the real location catches up
    const [prevPath, setPrevPath] = useState(location.pathname);
    if (location.pathname !== prevPath) {
        setPrevPath(location.pathname);
        setPendingId(undefined);
    }

    const selectItem = (id: NavPathOverrideEnum): void => {
        setPendingId(id);
    };

    // Resets settingsLevel itself: it is also passed as onSwitched to
    // ViewAsLevel, and vaul only calls onOpenChange for its own interaction
    // sources, never when `open` is driven programmatically like this is
    // after a view switch.
    const closeMore = (): void => {
        setMoreOpen(false);
        setSettingsLevel(0);
    };

    const onMoreOpenChange = (next: boolean): void => {
        setMoreOpen(next);
        if (!next) {
            setSettingsLevel(0);
        }
    };

    const allItems = navigation.flatMap((g) => g.items);

    // Top 4 direct-link items in desktop nav order — no hardcoded IDs
    const primaryItems = allItems
        .filter((item): item is MenuItem & { href: string } => !!item.href)
        .slice(0, 4);
    const primaryIdSet = new Set(primaryItems.map((i) => i.id));

    // Overflow preserves group structure (labels) from the desktop sidebar
    const overflowGroups = navigation
        .map((group) => ({
            id: group.id,
            label: group.label,
            items: group.items.filter((item) => !primaryIdSet.has(item.id)).flatMap(flattenItem),
        }))
        .filter((g) => g.items.length > 0);

    const isSelected = createIsSelected(pendingId ?? activeSection, location.pathname);
    const isMoreActive = !primaryItems.some((item) => isSelected(item));

    // The overflow nav links are identical in both sheet variants.
    const navLinks = overflowGroups.map((group) => (
        <div key={group.id}>
            {group.label && (
                <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                    {group.label}
                </p>
            )}
            {group.items.map((item) => (
                <Link
                    key={item.id}
                    to={item.href}
                    onClick={closeMore}
                    className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2.5',
                        isSelected(item)
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-neutral-950 hover:bg-neutral-100',
                    )}
                >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <Text color="inherit">{item.name}</Text>
                </Link>
            ))}
        </div>
    ));

    return (
        <Drawer.Root open={moreOpen} onOpenChange={onMoreOpenChange}>
            <nav
                aria-label={t('aria__main_navigation')}
                className="pointer-events-none fixed bottom-0 inset-x-0 z-40 flex justify-center sm:hidden"
            >
                {/* liquid glass pill */}
                <div
                    className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/40 px-2 py-2 shadow-2xl shadow-black/[0.10] backdrop-blur-3xl ring-1 ring-inset ring-white/50"
                    style={{ marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
                >
                    {primaryItems.map((item) => (
                        <Link
                            key={item.id}
                            to={item.href}
                            aria-label={item.name}
                            onClick={() => {
                                selectItem(item.id);
                                closeMore();
                            }}
                            className={cn(
                                'relative flex items-center justify-center rounded-full px-3 py-2 transition-colors duration-150 active:scale-90',
                                isSelected(item) ? 'bg-blue-600/10 text-blue-600' : 'text-neutral-500',
                            )}
                        >
                            <NavTab active={isSelected(item)} label={item.name} icon={item.icon} />
                        </Link>
                    ))}
                    <Drawer.Trigger asChild>
                        <button
                            type="button"
                            aria-label={t('mobile_nav__more')}
                            className={cn(
                                'relative flex items-center justify-center rounded-full px-3 py-2 transition-colors duration-150 active:scale-90',
                                isMoreActive ? 'bg-blue-600/10 text-blue-600' : 'text-neutral-500',
                            )}
                        >
                            <NavTab
                                active={isMoreActive}
                                label={t('mobile_nav__more')}
                                icon={MoreHorizontal}
                            />
                        </button>
                    </Drawer.Trigger>
                </div>
            </nav>

            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 z-50 bg-black/20" />
                <Drawer.Content className="fixed bottom-0 inset-x-0 z-50 rounded-t-xl bg-white pb-[env(safe-area-inset-bottom)] shadow-lg">
                    <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-neutral-300" />
                    <nav className="p-4">
                        <PushStack
                            activeIndex={settingsLevel}
                            levels={[
                                {
                                    key: 'nav',
                                    content: (
                                        <div className="flex flex-col gap-1">
                                            {navLinks}
                                            <IntercomLauncher />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    sendEvent('settings_menu_opened', {
                                                        surface: 'mobile_sheet',
                                                    });
                                                    setSettingsLevel(1);
                                                }}
                                                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-neutral-950 hover:bg-neutral-100"
                                            >
                                                <SettingsIcon className="h-4 w-4 shrink-0" />
                                                <Text color="inherit">{t('settings__button')}</Text>
                                                <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-neutral-500" />
                                            </button>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'settings',
                                    content: (
                                        <>
                                            <Menu className="p-0">
                                                <MenuBackRow
                                                    label={t('settings__button')}
                                                    onBack={() => {
                                                        setSettingsLevel(0);
                                                    }}
                                                />
                                            </Menu>
                                            <SettingsRows
                                                onOpenLevel={(level) => {
                                                    setSettingsLevel(settingsSubLevels.indexOf(level) + 2);
                                                }}
                                                onClose={closeMore}
                                            />
                                        </>
                                    ),
                                },
                                {
                                    key: 'language',
                                    content: (
                                        <LanguageLevel
                                            onBack={() => {
                                                setSettingsLevel(1);
                                            }}
                                        />
                                    ),
                                },
                                {
                                    key: 'defaultMetric',
                                    content: (
                                        <DefaultMetricLevel
                                            onBack={() => {
                                                setSettingsLevel(1);
                                            }}
                                        />
                                    ),
                                },
                                {
                                    key: 'defaultTimeframe',
                                    content: (
                                        <DefaultTimeframeLevel
                                            onBack={() => {
                                                setSettingsLevel(1);
                                            }}
                                        />
                                    ),
                                },
                                {
                                    key: 'viewAs',
                                    content: (
                                        <ViewAsLevel
                                            onBack={() => {
                                                setSettingsLevel(1);
                                            }}
                                            onSwitched={closeMore}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </nav>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
}
